import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  forwardRef,
} from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Input,
  Textarea,
  DatePicker,
  Label,
  Card,
  CardContent,
} from '@roger/ui-components';
import { updateOrderData, updateOrder, client } from '../../services/api';
import type { OrderResponse } from '../../services/api/types.gen';
import { parseISOToLocalDate, formatDateToISO } from '../../utils/dateUtils';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useToast } from '../../hooks/useToastContext';

export interface GenericOrderFormHandle {
  validate: () => Promise<boolean>;
  flush: () => Promise<void>;
}

interface GenericOrderFormProps {
  visit: OrderResponse;
  onSave?: () => void;
}

const schema = z.object({
  order_date: z.string().min(1, 'Order date is required'),
  order_time: z.string().optional(),
  order_text: z.string().min(1, 'Order text is required'),
  order_summary: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

function getExistingOrderData(
  orderData: OrderResponse['order_data']
): Record<string, unknown> {
  if (orderData && typeof orderData === 'object' && !Array.isArray(orderData)) {
    return orderData;
  }
  return {};
}

function visitToFormValues(visit: OrderResponse): FormValues {
  // Dedicated columns have priority; fall back to order_data JSONB
  const data = visit.order_data as Record<string, string> | null;
  return {
    order_date: visit.order_date || data?.order_date || '',
    order_time: visit.order_time || data?.order_time || '',
    order_text: visit.order_text || data?.order_text || '',
    order_summary: visit.order_summary || data?.order_summary || '',
  };
}

export const GenericOrderForm = forwardRef<
  GenericOrderFormHandle,
  GenericOrderFormProps
>(({ visit }, ref) => {
  const { error: showError } = useToast();
  const {
    control,
    register,
    watch,
    trigger,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: visitToFormValues(visit),
    mode: 'onTouched',
  });
  const latestOrderDataRef = useRef<Record<string, unknown>>(
    getExistingOrderData(visit.order_data)
  );
  const orderDataLastUpdatedRef = useRef<string | null>(
    visit.order_data_last_updated ?? null
  );

  // Reset the OCC refs only when navigation changes orders or the server
  // advances the token. order_data changes are expected to advance
  // order_data_last_updated, and including the JSONB object would wipe in-flight
  // edit state on autosave responses.
  useEffect(() => {
    latestOrderDataRef.current = getExistingOrderData(visit.order_data);
    orderDataLastUpdatedRef.current = visit.order_data_last_updated ?? null;
  }, [visit.id, visit.order_data_last_updated]);

  const performSave = useCallback(
    async (values: Partial<FormValues>) => {
      const orderData = {
        ...latestOrderDataRef.current,
        ...values,
      };

      const [orderDataResponse, orderResponse] = await Promise.all([
        updateOrderData({
          path: { order_id: visit.id },
          body: {
            order_data: orderData,
            order_data_last_updated: orderDataLastUpdatedRef.current,
          },
          client,
        }),
        updateOrder({
          path: { order_id: visit.id },
          body: {
            order_date: (values.order_date as string) || undefined,
            order_text: (values.order_text as string) || undefined,
            order_summary: (values.order_summary as string) || undefined,
            order_time: (values.order_time as string) || undefined,
          },
          client,
        }),
      ]);

      if (orderDataResponse.error || !orderDataResponse.data) {
        console.error('Failed to save order data', orderDataResponse.error);
        showError(
          getHttpStatus(orderDataResponse) === 409
            ? 'This order was updated elsewhere. Reload the order and try again.'
            : 'Failed to save order. Please try again.'
        );
        throw new Error('Failed to save order data');
      }

      latestOrderDataRef.current = getExistingOrderData(
        orderDataResponse.data.order_data
      );
      orderDataLastUpdatedRef.current =
        orderDataResponse.data.order_data_last_updated ??
        orderDataLastUpdatedRef.current;

      if (orderResponse.error) {
        console.error('Failed to save order fields', orderResponse.error);
        showError('Failed to save order. Please try again.');
        throw new Error('Failed to save order fields');
      }
    },
    [showError, visit.id]
  );

  const { setValue: setAutoSaveValue, saveNow } = useAutoSave({
    initialValue: JSON.stringify(visitToFormValues(visit)),
    onSave: useCallback(
      async (jsonValues: string) => {
        await performSave(JSON.parse(jsonValues) as FormValues);
      },
      [performSave]
    ),
    debounceDelay: 1500,
  });

  useEffect(() => {
    const subscription = watch(formValues => {
      setAutoSaveValue(JSON.stringify(formValues));
    });
    return () => subscription.unsubscribe();
  }, [watch, setAutoSaveValue]);

  useImperativeHandle(
    ref,
    () => ({
      validate: () => trigger(),
      flush: () => saveNow(),
    }),
    [trigger, saveNow]
  );

  return (
    <Card>
      <CardContent className='space-y-4'>
        {/* Order Date */}
        <div className='space-y-1.5'>
          <Label>
            Order Date <span className='text-red-500'>*</span>
          </Label>
          <Controller
            name='order_date'
            control={control}
            render={({ field }) => (
              <DatePicker
                date={
                  field.value
                    ? (parseISOToLocalDate(field.value) ?? undefined)
                    : undefined
                }
                onDateChange={d => field.onChange(d ? formatDateToISO(d) : '')}
                datePickerSize='sm'
              />
            )}
          />
          {errors.order_date && (
            <p className='text-xs text-red-500'>{errors.order_date.message}</p>
          )}
        </div>

        {/* Order Time */}
        <div className='space-y-1.5'>
          <Label>Order Time</Label>
          <Input
            {...register('order_time')}
            placeholder='HH:MM'
            inputSize='sm'
          />
        </div>

        {/* Order Text */}
        <div className='space-y-1.5'>
          <Label>
            Order Text <span className='text-red-500'>*</span>
          </Label>
          <Textarea
            {...register('order_text')}
            placeholder='Enter order text'
            textareaSize='sm'
          />
          {errors.order_text && (
            <p className='text-xs text-red-500'>{errors.order_text.message}</p>
          )}
        </div>

        {/* Order Summary */}
        <div className='space-y-1.5'>
          <Label>Order Summary</Label>
          <Textarea
            {...register('order_summary')}
            placeholder='Enter order summary'
            textareaSize='sm'
          />
        </div>
      </CardContent>
    </Card>
  );
});

GenericOrderForm.displayName = 'GenericOrderForm';
